import React, { useEffect, useState } from 'react';
import { FormControl, FormHelperText, Icon, TextField, Switch, FormControlLabel, InputLabel, Select, MenuItem } from '@mui/material';

import DialogBase from '../dialogs/DialogBase';
import { LayerViewModel } from '../../../models/maps/layerViewModel';

interface DialogPublicLayerProps {
  open: boolean;
  layer?: LayerViewModel | null;
  onClose: () => void;
  onSubmit: (layer: LayerViewModel) => void;
}

const MAX_DESCRIPTION_LENGTH = 2000;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 400,
    },
  },
};

const DialogPublicLayer: React.FC<DialogPublicLayerProps> = ({
  open,
  layer,
  onClose,
  onSubmit,
}) => {
  const [selectedLayer, setSelectedLayer] = useState<LayerViewModel>({} as LayerViewModel);
  const [charCount, setCharCount] = useState(0);

  const [validationErrors, setValidationErrors] = useState<{
    url?: string;
    sourceName?: string;
    layerName?: string;
    displayName?: string;
    type?: string;
    description?: string;
  }>({});

  useEffect(() => {
    if (!open) return;

    if (layer) {
      setSelectedLayer(layer);
      setCharCount(layer.description?.length || 0);
    } else {
      setSelectedLayer({
        id: "",
        sourceName: "",
        layerName: "",
        displayName: "",
        description: "",
        active: true,
        url: "",
        type: "wms",
        isAssetLayer: false,
      });

      setCharCount(0);
    }

    setValidationErrors({});
  }, [open, layer]);

  const onChange = <K extends keyof LayerViewModel>(key: K, value: LayerViewModel[K]) => {
    setSelectedLayer((prev: LayerViewModel) => ({ ...prev, [key]: value }));
  };

  const isHttpsUrl = (value: string) => {
    const v = (value ?? '').trim();
    if (!v) return false;
    if (!v.startsWith('https://')) return false;

    // Optional: basic URL validity check (keeps it lenient)
    try {
      // eslint-disable-next-line no-new
      new URL(v);
      return true;
    }
    catch {
      return false;
    }
  };

  const handleSubmit = () => {
    const errors: typeof validationErrors = {};

    // URL
    if (!selectedLayer.url || selectedLayer.url.trim() === '') {
      errors.url = 'URL is verplicht';
    } 
    else if (!isHttpsUrl(selectedLayer.url)) {
      errors.url = 'URL moet starten met https:// en een geldige URL zijn';
    }

    // Required strings
    if (!selectedLayer.sourceName || selectedLayer.sourceName.trim() === '') errors.sourceName = 'Bron is verplicht';
    if (!selectedLayer.layerName || selectedLayer.layerName.trim() === '') errors.layerName = 'Naam is verplicht';
    if (!selectedLayer.displayName || selectedLayer.displayName.trim() === '') errors.displayName = 'Schermnaam is verplicht';

    // Type (protocol)
    if (!selectedLayer.type || String(selectedLayer.type).trim() === '') errors.type = 'Type is verplicht';

    // Description length
    if (selectedLayer.description && selectedLayer.description.length > MAX_DESCRIPTION_LENGTH) {
      errors.description = 'Omschrijving mag maximaal 2000 tekens bevatten';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onSubmit({
      ...selectedLayer,
      url: selectedLayer.url?.trim() ?? '',
      sourceName: selectedLayer.sourceName?.trim() ?? '',
      layerName: selectedLayer.layerName?.trim() ?? '',
      displayName: selectedLayer.displayName?.trim() ?? '',
      description: selectedLayer.description?.trim() ?? '',
    });

    onClose();
  };

  return (
    <DialogBase
      open={open}
      title="Openbare kaartlaag toevoegen"
      contentText=""
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Opslaan"
      confirmButtonIcon={<Icon>save</Icon>}
      width="900px"
      minWidth="700px"
    >
      {/* Type */}
      <FormControl fullWidth required className="select" sx={{ my: 1 }}>
        <InputLabel id="select-type-label">Type</InputLabel>
        <Select
          labelId="select-type-label"
          id="type"
          variant="filled"
          value={(selectedLayer.type as any) || ''}
          onChange={(e) => onChange('type', e.target.value as any)}
          error={!!validationErrors.type}
          MenuProps={MenuProps}
        >
          <MenuItem value=""><em>&nbsp;</em></MenuItem>
          <MenuItem value="wms">WMS</MenuItem>
          {/* <MenuItem value="wfs">WFS</MenuItem> */}
        </Select>
        {validationErrors.type && (
          <FormHelperText error>{validationErrors.type}</FormHelperText>
        )}
      </FormControl>
      
      {/* Bron */}
      <FormControl fullWidth required sx={{ my: 1 }}>
        <TextField
          required
          label="Bron"
          variant="filled"
          value={selectedLayer.sourceName ?? ''}
          placeholder="Bijv. PDOK"
          onChange={(e) => onChange('sourceName', e.target.value)}
          error={!!validationErrors.sourceName}
          helperText={validationErrors.sourceName}
        />
      </FormControl>

      {/* Naam (layerName) */}
      <FormControl fullWidth required sx={{ my: 1 }}>
        <TextField
          required
          label="Naam"
          variant="filled"
          value={selectedLayer.layerName ?? ''}
          placeholder="Bijv. BAG Verblijfsobjecten"
          onChange={(e) => onChange('layerName', e.target.value)}
          error={!!validationErrors.layerName}
          helperText={validationErrors.layerName}
        />
      </FormControl>

      {/* Schermnaam (displayName) */}
      <FormControl fullWidth required sx={{ my: 1 }}>
        <TextField
          required
          label="Schermnaam"
          variant="filled"
          value={selectedLayer.displayName ?? ''}
          placeholder="Bijv. BAG - Verblijfsobjecten"
          onChange={(e) => onChange('displayName', e.target.value)}
          error={!!validationErrors.displayName}
          helperText={validationErrors.displayName}
        />
      </FormControl>

      {/* URL */}
      <FormControl fullWidth required sx={{ my: 1 }}>
        <TextField
          required
          label="URL (WFS / OGC API)"
          variant="filled"
          value={selectedLayer.url ?? ''}
          placeholder="https://service.pdok.nl/.../wfs/v1_0"
          onChange={(e) => onChange('url', e.target.value)}
          error={!!validationErrors.url}
          helperText={validationErrors.url}
        />
      </FormControl>

      {/* Omschrijving */}
      <FormControl fullWidth sx={{ my: 1 }}>
        <TextField
          label="Omschrijving"
          variant="filled"
          multiline
          rows={5}
          value={selectedLayer.description ?? ''}
          placeholder="Optioneel: korte toelichting voor beheer"
          onChange={(e) => {
            onChange('description', e.target.value);
            setCharCount(e.target.value.length);
          }}
          error={!!validationErrors.description}
          helperText={validationErrors.description}
          inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
        />
        <FormHelperText sx={{ textAlign: 'right' }}>
          {charCount} / {MAX_DESCRIPTION_LENGTH}
        </FormHelperText>
      </FormControl>
      
      {/* Actief */}
      <FormControl sx={{ my: 1 }}>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(selectedLayer.active)}
              onChange={(e) => onChange("active", e.target.checked)}
              color="success"
            />
          }
          label="Maak de kaartlaag actief"
        />
      </FormControl>
    </DialogBase>
  );
};

export default DialogPublicLayer;
